import type { EmbeddedSurface } from "@workspace/database/schema/chat";

/**
 * A **copilot** is a host-bound delivery vehicle for one or more capabilities
 * (Excel/Word/Outlook add-ins, the Procore side panel, …). This descriptor is
 * the single **node-safe** source of truth for everything node-reachable about
 * a copilot: which capabilities it delivers, which surfaces it runs on, its
 * per-surface embedded-host metadata (session cookies, context capture,
 * cross-site framing), and its `/copilots` launch + display metadata.
 *
 * The node-UNSAFE facets live in their own layers, keyed by surface and
 * reconciled with this descriptor by a completeness test:
 *   - the React **shell** → `apps/web/components/embedded-hosts.tsx`
 *   - the **ClientToolExecutorAdapter** → `apps/web/lib/office/client-tools.ts`
 *   - the host-context **renderer** → `@workspace/ai` `hostContextRenderers`
 *
 * HITL responsibilities live on the capability per ADR-050; this type carries
 * no approval contract.
 */

/**
 * Office add-ins share a single session cookie (one MSAL-minted session).
 *
 * The literal's source of truth is `@workspace/database`'s app-surface config,
 * where the Entra-exchange policy that *sets* this cookie also reads the name.
 * Re-exported here so copilot descriptors and the embedded-host readers
 * (`getSession()`, the proxy's `hasAnySessionCookie`) can never drift from the
 * writer.
 */
export { ADDIN_SESSION_COOKIE_NAME } from "@workspace/database/schema/chat/app-surfaces";

/**
 * Cookie + sessionStorage name for the Procore panel's session token
 * (`SameSite=None; Secure` — cross-site iframe). Set by
 * `/api/auth/procore-session`. Same string for both stores.
 */
export const PROCORE_SESSION_COOKIE_NAME = "magnus-procore-session-token";

/**
 * Cookie + sessionStorage name for the Freshservice panel's session token
 * (`SameSite=None; Secure` — cross-site iframe under `*.freshservice.com`).
 * Set by `/api/auth/freshservice-session`. Same string for both stores.
 */
export const FRESHSERVICE_SESSION_COOKIE_NAME =
  "magnus-freshservice-session-token";

/**
 * Per-surface embedded-host metadata — the node-safe facet that the session
 * stores, proxy, and auth routes key off. Consumed via the derived
 * `embeddedHostMeta` registry in `apps/web/lib/embedded-hosts.ts`.
 */
export type EmbeddedHostFacet = {
  /**
   * sessionStorage key for the generic surface-keyed session-token store.
   * Omit for hosts that manage their session bespoke (Office uses MSAL).
   */
  sessionStorageKey?: string;
  /**
   * Cookie name carrying this surface's raw session token, for RSC page
   * navigations that can't send a Bearer header.
   */
  sessionCookieName?: string;
  /**
   * True when the surface captures host context (and therefore must have a
   * matching `hostContextRenderers` entry server-side).
   */
  capturesContext?: boolean;
  /**
   * True for a cross-site iframe copilot that only ever runs *framed* inside
   * the host (Procore). A framed-only surface loaded top-level is a stale
   * marker leak; it also drives the `SameSite=None; Secure` cookie policy (the
   * `magnus-host` marker additionally carries CHIPS `Partitioned`). Office
   * add-ins are NOT framed-only.
   */
  framedOnly?: boolean;
};

/**
 * How a copilot is launched from the `/copilots` page CTA.
 * Pure data — must stay JSON-serializable (imported on client + server).
 */
export type CopilotLaunch =
  | { kind: "external-url"; url: string }
  | { kind: "protocol-handler"; uriTemplate: string }
  | { kind: "coming-soon" };

/** `/copilots` page display metadata. Present on copilots shown there. */
export type CopilotDisplay = {
  description: string;
  category: string;
  /** Lucide icon name — mapped to a component in the UI layer. */
  icon: string;
  /** Tailwind bg class for the copilot tile. */
  color: string;
  /** Fallback letter(s) for the icon placeholder. */
  initial: string;
};

export interface CopilotDefinition {
  /** Capability ids this copilot delivers (string ids — no import cycle). */
  capabilities: readonly string[];
  /** Stable identifier — React key / route segment / launch-registry key. */
  copilotId: string;
  /** `/copilots` display metadata. Absent → not shown on that page. */
  display?: CopilotDisplay;
  /** Display name (e.g. "Excel"). */
  displayName: string;
  /** Per-surface embedded-host metadata (session cookies, context, framing). */
  embedded?: Partial<Record<EmbeddedSurface, EmbeddedHostFacet>>;
  /** `/copilots` launch CTA. Absent → not launchable from that page. */
  launch?: CopilotLaunch;
  /** Subtitle / surface description (e.g. "Microsoft Excel add-in"). */
  surfaceLabel: string;
  /**
   * Surfaces this copilot appears on. Typed as `EmbeddedSurface` (not the
   * broader `AppSurface`): a copilot is by definition a host-bound delivery
   * vehicle, so the compiler now rejects a copilot declaring a non-embedded
   * surface — the invariant the "every copilot surface is a registered
   * embedded surface" runtime check used to guard.
   */
  surfaces: readonly EmbeddedSurface[];
}
